import React from 'react';
import {
  ChevronRightIcon,
  SparklesIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon
} from '@heroicons/react/24/outline';

const ModernCard = ({
  children,
  className = '',
  gradient = false,
  hover = true,
  onClick,
  ...props
}) => {
  const baseClasses = `
    rounded-2xl shadow-lg border border-blue-200/50 dark:border-blue-900/50 overflow-hidden transition-all duration-300 backdrop-blur-xl
    ${hover ? 'hover:shadow-xl hover:-translate-y-1' : ''}
    ${onClick ? 'cursor-pointer' : ''}
    ${gradient ? 'bg-gradient-to-br from-slate-600/85 dark:from-slate-800/85 to-slate-700/80 dark:to-blue-900/40' : 'bg-gradient-to-br from-slate-600/85 dark:from-slate-800/85 to-gray-700/80 dark:to-slate-900/40'}
    ${className}
  `;

  return (
    <div className={baseClasses} onClick={onClick} {...props}>
      {children}
    </div>
  );
};

export const ModernCardHeader = ({
  title,
  subtitle,
  icon: Icon,
  action,
  gradient = 'blue',
  className = ''
}) => {
  const gradientClasses = {
    blue: 'from-blue-600/80 to-indigo-600/70 border-blue-400/30',
    green: 'from-green-600/80 to-emerald-600/70 border-green-400/30',
    purple: 'from-purple-600/80 to-violet-600/70 border-purple-400/30',
    orange: 'from-orange-600/80 to-amber-600/70 border-orange-400/30',
    red: 'from-red-600/80 to-rose-600/70 border-red-400/30',
    indigo: 'from-indigo-600/80 to-purple-600/70 border-indigo-400/30',
    gray: 'from-slate-600/80 to-gray-600/70 border-slate-400/30'
  };

  return (
    <div className={`px-6 py-4 bg-gradient-to-r ${gradientClasses[gradient]} border-b backdrop-blur-sm ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {Icon && (
            <div className="w-10 h-10 bg-white/20 dark:bg-white/10 rounded-xl shadow-sm flex items-center justify-center backdrop-blur-sm">
              <Icon className="w-5 h-5 text-white" />
            </div>
          )}
          <div>
            <h3 className="text-lg font-semibold text-white">{title}</h3>
            {subtitle && <p className="text-sm text-white/80">{subtitle}</p>}
          </div>
        </div>
        {action && (
          <div className="flex items-center space-x-2">
            {action}
          </div>
        )}
      </div>
    </div>
  );
};

export const ModernCardBody = ({ children, className = '' }) => (
  <div className={`px-6 py-4 ${className}`}>
    {children}
  </div>
);

export const ModernCardFooter = ({ children, className = '' }) => (
  <div className={`px-6 py-4 bg-slate-600/60 dark:bg-slate-800/60 border-t border-slate-400/30 dark:border-slate-600/30 backdrop-blur-sm ${className}`}>
    {children}
  </div>
);

// Enhanced metric card for dashboards
export const ModernMetricCard = ({
  title,
  value,
  change,
  changeType,
  icon: Icon,
  trend = [],
  gradient = 'blue',
  onClick,
  loading = false
}) => {
  const gradientClasses = {
    blue: 'from-blue-500 to-indigo-600',
    green: 'from-green-500 to-emerald-600',
    purple: 'from-purple-500 to-violet-600',
    orange: 'from-orange-500 to-amber-600',
    red: 'from-red-500 to-rose-600',
    indigo: 'from-indigo-500 to-blue-600'
  };

  return (
    <ModernCard 
      onClick={onClick}
      className="group relative overflow-hidden"
    >
      {/* Gradient accent */}
      <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${gradientClasses[gradient]}`}></div>

      {/* Background decoration */}
      <div className="absolute top-4 right-4 w-16 h-16 bg-white/10 dark:bg-slate-700/30 rounded-full opacity-50 group-hover:opacity-70 transition-opacity duration-200"></div>

      <div className="relative p-6">
        <div className="flex items-start justify-between mb-4">
          <div className={`w-12 h-12 bg-gradient-to-br ${gradientClasses[gradient]} rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-200`}>
            <Icon className="w-6 h-6 text-white" />
          </div>

          {change && (
            <div className={`flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-semibold ${
              changeType === 'increase'
                ? 'text-green-100 bg-green-600/80 border border-green-400/30'
                : changeType === 'decrease'
                ? 'text-red-100 bg-red-600/80 border border-red-400/30'
                : 'text-gray-100 bg-gray-600/80 border border-gray-400/30'
            }`}>
              {changeType === 'increase' && <ArrowTrendingUpIcon className="w-4 h-4" />}
              {changeType === 'decrease' && <ArrowTrendingDownIcon className="w-4 h-4" />}
              <span>{change}</span>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <h3 className="text-sm font-medium text-gray-300 dark:text-gray-400 uppercase tracking-wide">{title}</h3>

          {loading ? (
            <div className="h-8 bg-gradient-to-r from-slate-600/50 via-slate-500/50 to-slate-600/50 rounded-lg animate-pulse"></div>
          ) : (
            <p className="text-3xl font-bold text-white dark:text-gray-100 group-hover:text-gray-100 transition-colors duration-200">{value}</p>
          )}
        </div>

        {/* Mini trend visualization */}
        {trend.length > 0 && (
          <div className="mt-4 flex items-end space-x-1 h-8">
            {trend.map((point, index) => (
              <div
                key={index}
                className={`bg-gradient-to-t ${gradientClasses[gradient]} rounded-sm opacity-60 group-hover:opacity-80 transition-opacity duration-200`}
                style={{ 
                  height: `${Math.max(10, (point / Math.max(...trend)) * 100)}%`,
                  width: '6px'
                }}
              ></div>
            ))}
          </div>
        )}

        {onClick && (
          <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <ChevronRightIcon className="w-5 h-5 text-gray-400" />
          </div>
        )}
      </div>
    </ModernCard>
  );
};

// Enhanced list item for modern lists
export const ModernListItem = ({
  title,
  subtitle,
  meta,
  avatar,
  status,
  actions = [],
  onClick,
  className = ''
}) => {
  return (
    <div
      className={`flex items-center justify-between p-4 hover:bg-gradient-to-r hover:from-blue-600/20 hover:to-indigo-600/20 transition-all duration-200 rounded-xl ${onClick ? 'cursor-pointer' : ''} ${className}`}
      onClick={onClick}
    >
      <div className="flex items-center space-x-4 flex-1">
        {avatar && (
          <div className="flex-shrink-0">
            {avatar}
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2">
            <h4 className="text-sm font-medium text-white dark:text-gray-100 truncate">{title}</h4>
            {status && (
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.className}`}>
                {status.label}
              </span>
            )}
          </div>
          {subtitle && (
            <p className="text-sm text-gray-300 dark:text-gray-400 truncate">{subtitle}</p>
          )}
          {meta && (
            <p className="text-xs text-gray-400 dark:text-gray-500">{meta}</p>
          )}
        </div>
      </div>

      {actions.length > 0 && (
        <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          {actions.map((action, index) => (
            <button
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                action.onClick();
              }}
              className={`p-2 rounded-lg transition-all duration-200 hover:scale-110 ${action.className || 'text-gray-300 hover:text-white hover:bg-white/10'}`}
              title={action.title}
            >
              <action.icon className="w-4 h-4" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Enhanced badge component - with glassmorphism and dark mode
export const ModernBadge = ({
  children,
  variant = 'default',
  size = 'md',
  className = ''
}) => {
  const variants = {
    default: 'bg-gray-600/80 dark:bg-gray-700/80 text-gray-100 border-gray-400/30 dark:border-gray-600/30',
    success: 'bg-green-600/80 dark:bg-green-700/80 text-green-100 border-green-400/30 dark:border-green-600/30',
    warning: 'bg-yellow-600/80 dark:bg-yellow-700/80 text-yellow-100 border-yellow-400/30 dark:border-yellow-600/30',
    danger: 'bg-red-600/80 dark:bg-red-700/80 text-red-100 border-red-400/30 dark:border-red-600/30',
    info: 'bg-blue-600/80 dark:bg-blue-700/80 text-blue-100 border-blue-400/30 dark:border-blue-600/30',
    purple: 'bg-purple-600/80 dark:bg-purple-700/80 text-purple-100 border-purple-400/30 dark:border-purple-600/30'
  };

  const sizes = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base'
  };

  return (
    <span className={`inline-flex items-center rounded-full font-semibold border backdrop-blur-sm shadow-sm ${variants[variant]} ${sizes[size]} ${className}`}>
      {children}
    </span>
  );
};

export default ModernCard;
